import type { NavigationCtasResolved, NavigationCtaItem } from './types';
import { Button } from '@/components/Button';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { substituteHref } from '@/lib/render/substituteHref';
import { toLiveHref } from '@/lib/toLiveHref';

const FOCUS_CLASS =
  'focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none';

// `outlined` and `primary`: one CTA per row on mobile, three per row from `md`,
// each filling its third of the content width, so every CTA in the widget comes
// out the same width. A trailing partial row stays left-aligned. The two styles
// share one grid string rather than repeating it, because AC 4 defines
// `primary`'s layout as being `outlined`'s.
// `filled`: a centred row of CTAs, each hugging its own label; `flex-wrap`
// wraps the row while `whitespace-nowrap` on the item keeps each label on one
// line (10.03.03.28 § AC 2-5).
const GRID = 'grid grid-cols-1 gap-4 md:grid-cols-3';

const CONTAINER = {
  outlined: GRID,
  filled: 'flex flex-wrap justify-center gap-3',
  primary: GRID,
} as const;

// `outlined` labels are capped by their third of the row, so a label too long
// for its pill is cut with an ellipsis instead of spilling past the border
// (10.03.03.28 § AC 2). `truncate` needs a block box to apply its ellipsis —
// hence `block` rather than the `flex` centring the other style uses; with a
// single-line label, `text-center` plus the vertical padding centres it
// identically. `truncate` also brings the `whitespace-nowrap` of AC 4.
// `filled` pills hug their label and the row wraps, so nothing overflows
// there and the label needs no cap. `primary` has no entry: it renders through
// the shared `Button`, which brings its own box (AC 4).
const ITEM = {
  filled: `bg-secondary-lighter text-foreground hover:text-primary inline-flex items-center rounded-2xl px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${FOCUS_CLASS}`,
} as const;

/**
 * `outlined` on top of the shared `Button` (SH-474).
 *
 * It used to be a hand-rolled pill whose height was `py-3` plus a `text-lg/6`
 * line box — 50px, the shortest CTA on the page and the one the ticket is
 * about. Through `Button` it takes `--button-height` and so cannot drift from
 * every other CTA again.
 *
 * What has to be carried over by hand:
 * - the **colours**, which are `foreground` here and `primary` in `Button`'s
 *   `secondary` variant;
 * - `text-lg/6`, which 10.03.03.28 keeps, **with its `md:` twin** — `classNames`
 *   resolves Tailwind conflicts through `twMerge`, but an unprefixed class
 *   never overrides a `md:`-prefixed one, so `md:text-md/8` would otherwise win
 *   from that breakpoint up.
 *
 * `FOCUS_CLASS` is **not** carried over: `Button`'s own focus ring is the same
 * four utilities. `px-5` is not either — `Button`'s `md` size already has it.
 */
const OUTLINED_CLASS =
  'border-foreground text-foreground w-full text-lg/6 md:text-lg/6';

interface ResolvedCta {
  label: string;
  href: string;
  target?: NavigationCtaItem['target'];
}

export function NavigationCtasComponent({
  resolved,
}: {
  resolved: NavigationCtasResolved;
}) {
  const sub = useSubstitute();
  const style = resolved.style ?? 'outlined';

  // A CTA needs both a label and a target to render at all; substitution runs
  // first so a placeholder that resolves to an empty string drops the CTA too.
  const ctas = (resolved.ctas ?? []).reduce<ResolvedCta[]>((acc, cta) => {
    const label = cta.label ? sub(cta.label).trim() : '';
    const href = substituteHref(cta.href, sub);
    if (label && href) {
      acc.push({ label, href, target: cta.target });
    }
    return acc;
  }, []);

  if (ctas.length === 0) {
    return null;
  }

  return (
    // No container of its own (70.14-Widget-Width): the SlotRenderer wrapper
    // supplies the measure and the gutter, so the CTA row lines up with its
    // neighbours at every breakpoint without restating either.
    <nav className="pt-4">
      {/* `list-none p-0` / `m-0` override the base `ul { list-disc pl-5 }` and
          `li { my-1 }` rules in globals.css: this is a CTA row, not prose, and
          the inherited 20px left padding would offset the whole row. */}
      <ul className={`list-none p-0 ${CONTAINER[style]}`}>
        {ctas.map((cta, i) => {
          const targetProps =
            cta.target === '_blank'
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {};
          return (
            <li key={`${cta.href}-${i}`} className="m-0 flex">
              {style === 'primary' ? (
                // `Button` applies `toLiveHref` itself, so it gets the raw href.
                // `w-full` makes it fill its third of the grid; `Button` sets no
                // width of its own, so nothing conflicts with it (which matters
                // here, as `classNames` is a plain join with no Tailwind
                // conflict resolution). `text-base/6` does collide with
                // `Button`'s own `text-sm/5`, and a plain join leaves the winner
                // to stylesheet order, so it carries `!` — the same way
                // `olc-form` and `offer-widget` bump a `Button`'s label size.
                // The label is capped by the cell exactly
                // as the outlined one is, so it truncates the same way — but on
                // an inner span, because `Button` is a flex container and
                // `truncate` only ellipsises a block box. `overflow-hidden`
                // inside `truncate` is also what lets the span shrink below its
                // content width as a flex item.
                <Button
                  href={cta.href}
                  variant="primary"
                  className="w-full text-base/6!"
                  {...targetProps}
                >
                  <span className="truncate">{cta.label}</span>
                </Button>
              ) : style === 'outlined' ? (
                // Same shape as the `primary` branch above, and for the same
                // reason: `Button` is a flex container, so the label truncates
                // on an inner block span rather than on the control itself.
                <Button
                  href={cta.href}
                  variant="secondary"
                  className={OUTLINED_CLASS}
                  {...targetProps}
                >
                  <span className="truncate">{cta.label}</span>
                </Button>
              ) : (
                <a
                  href={toLiveHref(cta.href)}
                  className={ITEM[style]}
                  {...targetProps}
                >
                  {cta.label}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
